/**
 * -----------------------------------------------------------------------------
 * Role Details Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a role in the organization.
 */

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useDeleteOrganiaztionRoleMutation,
  useEditOrganizationRoleMutation,
  useOrganizationRoleACLQuery,
  useOrganizationRolesQuery,
  useOrganizationUsersQuery,
  useSingleOrganizationRoleQuery,
} from "../../../api/network/adminApiServices";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { LegacyUserEntityListingPayload } from "../../../api/types/User";
import { TLegacyUserRole } from "../../../api/types/UserRole";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData, useLoggedInUserPerms } from "../../../utils/user";
import AdminsRolesCloneToNew from "../../admins/roles/cloneRole";
import { RoleAccessControlForm } from "./access-control-form";
import { ROLE_ACCESS_LEVELS } from "./constants";
import ModalAddUsersToRole from "./modalAddUsersToRole";
import { RoleDetailForm, RoleUsersForm } from "./role-form";
import {
  TFormFieldNames,
  roleDetailsInitialValues,
  roleDetailsSysadminValidationSchema,
  roleDetailsYupValidationSchema,
} from "./validation";

const ScreenAdminDetailRole = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.roles",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.roles.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { roles: orgRolesQueryParam } = listingQueryParams;

  // flag to idenfiy if user is coming from create new user popup
  // const isNewEntity = React.useRef<boolean>(!!locationState?.new);
  // making isNewEntity a state to let it change when locationState changes, esp. in case of cloning role
  const isNewEntity = !!locationState?.new;

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(!!isNewEntity);

  // prepare query params for fetching organization roles
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        roles: { ...orgRolesQueryParam, page: 1, search: value },
      })
    );
  }, 500);

  const roleQueryPayload = { ...orgRolesQueryParam };
  // Explicity remove accountid from query params to get both system and user roles
  delete roleQueryPayload.accountid;

  // fetch organization roles
  const { data: dataOrgRoles, isFetching: isFetchingOrgRoles } =
    useOrganizationRolesQuery(roleQueryPayload, {
      refetchOnMountOrArgChange: true,
    });
  const { results, count } = dataOrgRoles || {};

  // fetch single role details
  const {
    data: dataSingleRole,
    isFetching: isFetchingSingleRole,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationRoleQuery(
    {
      account_id: `${thisUserOrganizationId}`,
      role_id: `${roleId}`,
    },
    {
      skip: !roleId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
      ) {
        toast.error(t("toast.role_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.roles);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  // fetch role ACLs
  const [roleACLQueryParam, setRoleACLQueryParam] =
    useState<LegacyUserEntityListingPayload>({
      ...orgRolesQueryParam,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      roleid: roleId,
    });
  useEffect(() => {
    setRoleACLQueryParam({
      ...roleACLQueryParam,
      roleid: roleId,
    });
  }, [roleId]);
  const {
    data: dataOrgRoleACL,
    isFetching: isFetchingOrgRoleACL,
    error: errorOrgRoleACL,
  } = useOrganizationRoleACLQuery(roleACLQueryParam, {
    skip: !dataSingleRole || !roleId,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  // fetch role users
  const [roleUsersQueryParam, setRoleUsersQueryParam] =
    useState<LegacyUserEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      roleid: roleId,
    });
  useEffect(() => {
    setRoleUsersQueryParam({
      ...roleUsersQueryParam,
      roleid: roleId,
    });
  }, [roleId]);
  const { data: dataRoleUsers, isFetching: isFetchingRoleUsers } =
    useOrganizationUsersQuery(roleUsersQueryParam, {
      skip: !dataSingleRole || !roleId,
    });

  // role mutations
  const [editOrganizationRoleApiTrigger, { isLoading: isLoadingEditRole }] =
    useEditOrganizationRoleMutation();
  const [deleteRoleApiTrigger, { isLoading: isLoadingDeleteRole }] =
    useDeleteOrganiaztionRoleMutation();

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(
      !isFetchingSingleRole && !isFetchingOrgRoleACL && !isFetchingRoleUsers
    );
  }, [isFetchingSingleRole || isFetchingOrgRoleACL || isFetchingRoleUsers]);
  useEffect(() => {
    setFormikValuesReady(false);
  }, [roleId]);

  const { loggedInUserIsSysAdmin } = useLoggedInUserPerms();

  const formik = useFormik({
    initialValues: roleDetailsInitialValues,
    validationSchema:
      loggedInUserIsSysAdmin() === true
        ? roleDetailsSysadminValidationSchema
        : roleDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      let preparedRoleEditParam = {
        accountid: dataSingleRole?.accountid as string,
        roleid: values.role_id,
        description: values.description,
        role_acls: values.role_acls,
      };

      // call api for updating role
      editOrganizationRoleApiTrigger(preparedRoleEditParam)
        .unwrap()
        .then(() => {
          toast.success(t("toast.role_updated"));
          navigate(routeUrls.dashboardChildren.adminChildren.roles);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (
      dataSingleRole &&
      !isFetchingSingleRole &&
      !isFetchingOrgRoleACL &&
      !isFetchingRoleUsers
    ) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        role_id: dataSingleRole?.roleid || "",
        description: dataSingleRole?.description || "",
        role_acls:
          !!errorOrgRoleACL &&
          (errorOrgRoleACL as FetchBaseQueryError)?.status ===
            RESPONSE_CODE.BAD_REQUEST
            ? []
            : dataOrgRoleACL || [],
        users: dataRoleUsers?.results || [],
        searchACL: values.searchACL || "",
      });
      setUserCanEdit(!!isNewEntity);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [
    dataSingleRole,
    isFetchingSingleRole,
    dataOrgRoleACL,
    isFetchingOrgRoleACL,
    errorOrgRoleACL,
    dataRoleUsers,
    isFetchingRoleUsers,
  ]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;

  const existingUserIds = React.useMemo(() => {
    return values.users.map((user) => user.userid);
  }, [values]);

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  // handle edit role
  const handleEditRole = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  // handle delete role
  const handleDeleteRole = () => {
    deleteRoleApiTrigger({
      accountid: `${dataSingleRole?.accountid}`,
      roleid: `${dataSingleRole?.roleid}`,
    })
      .unwrap()
      .then(() => {
        toast.success(t("toast.role_deleted"));
        navigate(routeUrls.dashboardChildren.adminChildren.roles);
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  // update search keyword for ACL
  const debouncedSetSearchACL = useDebouncedCallback((value: string) => {
    formik.setFieldValue("searchACL", value);
  }, 500);

  const onBackButtonClick = () => {
    navigate(routeUrls.dashboardChildren.adminChildren.roles);
  };

  const handleAddUsersToRoleClick = (
    e: React.MouseEvent<HTMLElement> | undefined
  ) => {
    e?.stopPropagation();
    dispatch(setModalsData({ ...modalsState, showAddUsersToRoleModal: true }));
  };

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            ...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE) >=
            ROLE_ACCESS_LEVELS.ALL
              ? ([
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "primary",
                    label: tMain("clone"),
                    onClick: () => {
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showCloneRole: true,
                        })
                      );
                    },
                    disabled:
                      isFetchingSingleRole ||
                      isFetchingOrgRoleACL ||
                      isLoadingEditRole ||
                      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE) <
                        ROLE_ACCESS_LEVELS.ALL,
                  },
                ] as FormActionButton[])
              : []),
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "danger",
              label: tMain("delete"),
              onClick: () => {
                // check if dataRoleUsers?.results?.length > 0 and show warning message
                if (!!dataRoleUsers?.results?.length) {
                  toast.error(t("toast.role_has_users"));
                  return;
                }
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showDeleteConfirmation: true,
                  })
                );
              },
              disabled:
                isFetchingSingleRole ||
                isFetchingOrgRoleACL ||
                isLoadingEditRole ||
                dataSingleRole?.is_system_role ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE) <
                  ROLE_ACCESS_LEVELS.ALL,
            },
            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "secondary",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                  } as FormActionButton,
                ]
              : []),
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditRole
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingSingleRole ||
                isFetchingOrgRoleACL ||
                isLoadingEditRole ||
                (userCanEdit && !!Object.keys(errors).length) ||
                dataSingleRole?.is_system_role ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE) <
                  ROLE_ACCESS_LEVELS.WRITE,
            },
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleRole,
    isLoadingEditRole,
    userCanEdit,
    errors,
    isFetchingOrgRoleACL,
    dataSingleRole?.is_system_role,
    dataRoleUsers?.results?.length,
    handleEditRole,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditRole(),
              disabled: isLoadingEditRole || !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditRole, errors, userCanEdit, handleEditRole]);
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center">
          <p className="font-semibold text-accent-blue-dark text-lg leading-6">
            {t("sub_heading")}
          </p>
        </div>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgRoles ? " opacity-40 pointer-events-none" : ""}${isNewEntity ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgRolesQueryParam.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {dataOrgRoles?.results?.map(
                (item: TLegacyUserRole, index: number) => (
                  <AdminListingColumnItem
                    key={index}
                    selected={roleId === item.roleid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.adminChildren.roles}/${item.roleid}`
                      )
                    }
                    title={item.roleid}
                    description={item.description}
                    asideText={
                      item.total_users === 0
                        ? "-"
                        : t(item.total_users === 1 ? "n_user" : "n_users", {
                            count: item.total_users,
                          })
                    }
                    bottomText={""}
                  />
                )
              )}
            </div>
            {!isFetchingOrgRoles && (
              <Pagination
                pageSize={orgRolesQueryParam.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      roles: {
                        ...orgRolesQueryParam,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count ? Math.ceil(count / orgRolesQueryParam.page_size) : 1
                }
                forcePage={orgRolesQueryParam.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      roles: {
                        ...orgRolesQueryParam,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleRole ||
                    isFetchingOrgRoleACL ||
                    (isFetchingOrgRoles && (
                      <img
                        src={svgIcons.LoadingAnimation}
                        alt="loading-animation"
                        className="w-8 h-8"
                      />
                    ))}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditRole}
                      disabled={isLoadingEditRole || !!Object.keys(errors).length}
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleRole && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => {
                        // check if dataRoleUsers?.results?.length > 0 and show warning message
                        if (!!dataRoleUsers?.results?.length) {
                          toast.error(t("toast.role_has_users"));
                          return;
                        }
                        dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true }))
                      }}
                      disabled={isFetchingSingleRole || isFetchingOrgRoleACL || isLoadingEditRole || dataSingleRole?.is_system_role || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE) < ROLE_ACCESS_LEVELS.ALL}
                    />
                  </div>
                  {userCanEdit && (<div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="secondary"
                      label={tMain("cancel")}
                      onClick={onBackButtonClick}
                    />
                  </div>)}
                  <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={userCanEdit ? handleEditRole : () => setUserCanEdit(!userCanEdit)}
                      disabled={isFetchingSingleRole || isFetchingOrgRoleACL || isLoadingEditRole || (userCanEdit && !!Object.keys(errors).length) || dataSingleRole?.is_system_role || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE) < ROLE_ACCESS_LEVELS.WRITE}
                    />
                  </div> */}
                  {MemoizedFormActionButtons}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleRole || isLoadingEditRole || isLoadingDeleteRole ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian title={t("accord_details")} openByDefault={true}>
                  <RoleDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={isFetchingSingleRole || !formikValuesReady}
                  />
                </Accordian>
                {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && formikValuesReady && (
                  <div className="absolute text-xs">
                    <p>error: {JSON.stringify(errorOrgRoleACL)}</p>
                    <p>
                      typeof error: {JSON.stringify(typeof errorOrgRoleACL)}
                    </p>
                    <p>
                      values.role_acls:{" "}
                      {JSON.stringify(values.role_acls.length)}
                    </p>
                    <p>
                      dataOrgRoleACL: {JSON.stringify(dataOrgRoleACL?.length)}
                    </p>
                  </div>
                )}
                <Accordian title={t("accord_role_access_control")}>
                  <RoleAccessControlForm // {formikValuesReady && (
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleRole ||
                      isFetchingOrgRoleACL ||
                      !formikValuesReady
                    }
                  />
                </Accordian>
                {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && formikValuesReady && (
                  <div className="absolute text-xs">
                    <p>values.users: {JSON.stringify(values.users.length)}</p>
                    <p>
                      dataRoleUsers:{" "}
                      {JSON.stringify(dataRoleUsers?.results?.length)}
                    </p>
                  </div>
                )}
                <Accordian
                  title={
                    <div className="flex justify-between w-full cursor-pointer">
                      <span>
                        {t("accord_users", { count: dataRoleUsers?.count })}
                      </span>
                      {getUserACLAccessLevel(
                        ACL_NAMES.ACL_ADMIN_ROLE_ASSIGNTOUSER
                      ) >= ROLE_ACCESS_LEVELS.WRITE && (
                        <AdminFormFieldSubmit
                          customWrapperClass={"col-span-6 mr-4"}
                          type="button"
                          variant="primary-alt"
                          label={
                            <span className="flex justify-center pl-2 tracking-tight whitespace-nowrap">
                              {t("add_users")}
                              <img
                                className="ml-2"
                                src={svgIcons.ButtonIconPlus}
                                alt={t("add_users")}
                              />
                            </span>
                          }
                          onClick={handleAddUsersToRoleClick}
                        />
                      )}
                    </div>
                  }
                >
                  <RoleUsersForm // {formikValuesReady && (
                    roleUsersQueryParam={roleUsersQueryParam}
                    setRoleUsersQueryParam={setRoleUsersQueryParam}
                    dataRoleUsers={dataRoleUsers}
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleRole ||
                      isFetchingRoleUsers ||
                      !formikValuesReady
                    }
                  />
                </Accordian>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteRole} />
      {!isFetchingSingleRole && modalsState.showAddUsersToRoleModal && (
        <ModalAddUsersToRole
          roleId={roleId || ""}
          existingUsers={existingUserIds}
        />
      )}
      {!!dataSingleRole && (
        <AdminsRolesCloneToNew
          originalRole={dataSingleRole}
          key={`clone-to-new-${roleId}`}
        />
      )}
    </>
  );
};

export default ScreenAdminDetailRole;
